import { getStateOptions } from '@/helpers/getStateOptions';
import { Box, Button, SelectInput, SimulatedEventPayloadType, TextInput } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface LeadFilters {
  states: string[];
  minBatteryKwh: string;
  maxBatteryKwh: string;
  minSystemSize: string;
  maxSystemSize: string;
  minPricePerWatt: string;
  maxPricePerWatt: string;
  dateFrom: string;
  dateTo: string;
}

interface LeadManagementFilterPanelProps {
  onClose: () => void;
  onApply: (filters: LeadFilters) => void;
  currentFilters: LeadFilters;
}

export const EMPTY_FILTERS: LeadFilters = {
  states: [],
  minBatteryKwh: '',
  maxBatteryKwh: '',
  minSystemSize: '',
  maxSystemSize: '',
  minPricePerWatt: '',
  maxPricePerWatt: '',
  dateFrom: '',
  dateTo: '',
};

const STATE_OPTIONS = getStateOptions();
const STATE_OPTIONS_BY_VALUE: Record<string, { label: string; value: string }> = STATE_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.value] = opt;
    return acc;
  },
  {} as Record<string, { label: string; value: string }>,
);

const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;
const scrollableStyle: React.CSSProperties = { maxHeight: '400px', overflowY: 'auto' };

export const LeadManagementFilterPopover = ({
  onClose,
  onApply,
  currentFilters,
}: LeadManagementFilterPanelProps) => {
  const [filters, setFilters] = useState<LeadFilters>(currentFilters);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const stateValue = useMemo(
    () => filters.states.map((s) => STATE_OPTIONS_BY_VALUE[s] ?? { label: s, value: s }),
    [filters.states],
  );

  const handleStatesChange = useCallback((event: SimulatedEventPayloadType) => {
    const value = event.target.value;
    const next = Array.isArray(value) ? value.map((v: { value: string }) => v.value) : [];
    setFilters((prev) => ({ ...prev, states: next }));
  }, []);

  const handleMinBatteryKwhChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, minBatteryKwh: e.target.value }));
  }, []);

  const handleMaxBatteryKwhChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, maxBatteryKwh: e.target.value }));
  }, []);

  const handleMinSystemSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, minSystemSize: e.target.value }));
  }, []);

  const handleMaxSystemSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, maxSystemSize: e.target.value }));
  }, []);

  const handleMinPricePerWattChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, minPricePerWatt: e.target.value }));
  }, []);

  const handleMaxPricePerWattChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, maxPricePerWatt: e.target.value }));
  }, []);

  const handleDateFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, dateFrom: e.target.value }));
  }, []);

  const handleDateToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, dateTo: e.target.value }));
  }, []);

  const handleApply = useCallback(() => onApply(filters), [filters, onApply]);

  const handleClear = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  }, [onApply]);

  return (
    <Box direction="column" background="primary">
      <Box direction="column" childGap="sm" padding="sm" style={scrollableStyle}>
        <Box direction="column" childGap="2xs">
          <Box as="span" fontSize="sm" fontWeight="bold" color="body-primary">
            State
          </Box>
          <SelectInput
            id="filter-states"
            name="states"
            label="State"
            hideLabel
            value={stateValue}
            onChange={handleStatesChange}
            options={STATE_OPTIONS}
            menuPortalTarget={menuPortalTarget}
            isMulti
            placeholder="Select states..."
          />
        </Box>

        <Box direction="column" childGap="2xs">
          <Box as="span" fontSize="sm" fontWeight="bold" color="body-primary">
            Battery (kWh)
          </Box>
          <Box direction="row" childGap="xs">
            <TextInput
              id="filter-min-battery"
              size="sm"
              label="Min"
              hideLabel
              placeholder="Min"
              type="number"
              value={filters.minBatteryKwh}
              onChange={handleMinBatteryKwhChange}
              suffix="kWh"
            />
            <TextInput
              id="filter-max-battery"
              size="sm"
              label="Max"
              hideLabel
              placeholder="Max"
              type="number"
              value={filters.maxBatteryKwh}
              onChange={handleMaxBatteryKwhChange}
              suffix="kWh"
            />
          </Box>
        </Box>

        <Box direction="column" childGap="2xs">
          <Box as="span" fontSize="sm" fontWeight="bold" color="body-primary">
            System size (kW)
          </Box>
          <Box direction="row" childGap="xs">
            <TextInput
              id="filter-min-size"
              size="sm"
              label="Min"
              hideLabel
              placeholder="Min"
              type="number"
              value={filters.minSystemSize}
              onChange={handleMinSystemSizeChange}
              suffix="kW"
            />
            <TextInput
              id="filter-max-size"
              size="sm"
              label="Max"
              hideLabel
              placeholder="Max"
              type="number"
              value={filters.maxSystemSize}
              onChange={handleMaxSystemSizeChange}
              suffix="kW"
            />
          </Box>
        </Box>

        <Box direction="column" childGap="2xs">
          <Box as="span" fontSize="sm" fontWeight="bold" color="body-primary">
            Price per watt
          </Box>
          <Box direction="row" childGap="xs">
            <TextInput
              id="filter-min-ppw"
              size="sm"
              label="Min"
              hideLabel
              placeholder="Min"
              type="number"
              value={filters.minPricePerWatt}
              onChange={handleMinPricePerWattChange}
              prefix="$"
            />
            <TextInput
              id="filter-max-ppw"
              size="sm"
              label="Max"
              hideLabel
              placeholder="Max"
              type="number"
              value={filters.maxPricePerWatt}
              onChange={handleMaxPricePerWattChange}
              prefix="$"
            />
          </Box>
        </Box>

        <Box direction="column" childGap="2xs">
          <Box as="span" fontSize="sm" fontWeight="bold" color="body-primary">
            Date range
          </Box>
          <Box direction="column" childGap="2xs">
            <TextInput
              id="filter-date-from"
              size="sm"
              label="From"
              hideLabel
              placeholder="From"
              type="date"
              value={filters.dateFrom}
              onChange={handleDateFromChange}
            />
            <TextInput
              id="filter-date-to"
              size="sm"
              label="To"
              hideLabel
              placeholder="To"
              type="date"
              value={filters.dateTo}
              onChange={handleDateToChange}
            />
          </Box>
        </Box>
      </Box>

      <Box
        direction="row"
        justifyContent="flex-end"
        childGap="xs"
        borderWidth="xs 0 0 0"
        borderColor="separator"
        padding="sm"
      >
        <Button type="button" variant="tertiary" size="sm" onClick={handleClear}>
          Clear
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Box>
  );
};
